import { https } from "@/lib/fetchService";
import type { OrgType } from "@/types/orgTypes";

export interface Member {
  id: string;
  userId: string;
  role: "owner" | "admin" | "member";
  email: string;
  name: string;
  createdAt: string;
}

export interface GetMembersResponse {
  success: boolean;
  data: Member[];
}

export interface Organization {
  id: string;
  name: string;
  slug: string | null;
  logo: string | null;
  type: OrgType | null;
  address: string | null;
  website: string | null;
  contactPhone: string | null;
  createdAt: string;
}

export interface GetOrganizationResponse {
  success: boolean;
  data: Organization;
}

export interface UpdateOrganizationPayload {
  name?: string;
  logo?: string | null;
  address?: string | null;
  website?: string | null;
  contactPhone?: string | null;
}

export interface Factory {
  id: string;
  organizationId: string;
  name: string;
  factoryCode: string;
  factoryTag: string | null;
  description: string | null;
  location: string | null;
  address: string | null;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string | null;
  deletedAt: string | null;
}

export interface GetFactoriesResponse {
  success: boolean;
  data: Factory[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface GetFactoryResponse {
  success: boolean;
  data: Factory;
}

export interface CreateFactoryPayload {
  name: string;
  factoryCode: string;
  factoryTag?: string;
  description?: string;
  location?: string;
  address?: string;
}

export type UpdateFactoryPayload = Partial<CreateFactoryPayload>;

export const getMembers = (orgId: string) =>
  https.get<GetMembersResponse>({ url: `/v1/organizations/${orgId}/members` });

export interface MemberLookupEntry {
  memberId: string;
  userId: string;
  name: string | null;
}

export interface GetMembersLookupResponse {
  success: boolean;
  data: MemberLookupEntry[];
}

export const getMembersLookup = (orgId: string) =>
  https.get<GetMembersLookupResponse>({
    url: `/v1/organizations/${orgId}/members/lookup`,
  });

export const inviteMember = (orgId: string, email: string, role: string) =>
  https.post({
    url: `/v1/organizations/${orgId}/members/invite`,
    body: { email, role },
  });

export const revokeMember = (orgId: string, memberId: string) =>
  https.delete({ url: `/v1/organizations/${orgId}/members/${memberId}` });

export const updateMemberAccess = (
  orgId: string,
  memberId: string,
  role: string
) =>
  https.put({
    url: `/v1/organizations/${orgId}/members/${memberId}/access`,
    body: { role },
  });

export const getOrganization = (orgId: string) =>
  https.get<GetOrganizationResponse>({ url: `/v1/organizations/${orgId}` });

export const updateOrganization = (
  orgId: string,
  payload: UpdateOrganizationPayload
) =>
  https.put<GetOrganizationResponse>({
    url: `/v1/organizations/${orgId}`,
    body: payload,
  });

export const getFactories = (orgId: string, page = 1, limit = 10) =>
  https.get<GetFactoriesResponse>({
    url: `/v1/organizations/${orgId}/factories?page=${page}&limit=${limit}`,
  });

export const getFactory = (orgId: string, factoryId: string) =>
  https.get<GetFactoryResponse>({
    url: `/v1/organizations/${orgId}/factories/${factoryId}`,
  });

export const createFactory = (orgId: string, payload: CreateFactoryPayload) =>
  https.post<GetFactoryResponse>({
    url: `/v1/organizations/${orgId}/factories`,
    body: payload,
  });

export const updateFactory = (
  orgId: string,
  factoryId: string,
  payload: UpdateFactoryPayload
) =>
  https.put<GetFactoryResponse>({
    url: `/v1/organizations/${orgId}/factories/${factoryId}`,
    body: payload,
  });

export const deleteFactory = (orgId: string, factoryId: string) =>
  https.delete({ url: `/v1/organizations/${orgId}/factories/${factoryId}` });

// ---------------------------------------------------------------------------
// Production Lines
// ---------------------------------------------------------------------------

export interface ProductionLine {
  id: string;
  organizationId: string;
  factoryId: string;
  name: string;
  lineCode: string;
  description: string | null;
  status: "active" | "inactive";
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string | null;
  deletedAt: string | null;
}

export interface GetProductionLinesResponse {
  success: boolean;
  data: ProductionLine[];
}

export interface GetProductionLineResponse {
  success: boolean;
  data: ProductionLine;
}

export interface CreateProductionLinePayload {
  name: string;
  lineCode: string;
  description?: string;
  status?: "active" | "inactive";
}

export type UpdateProductionLinePayload = Partial<CreateProductionLinePayload>;

export const getProductionLines = (orgId: string, factoryId: string) =>
  https.get<GetProductionLinesResponse>({
    url: `/v1/organizations/${orgId}/factories/${factoryId}/production-lines`,
  });

export const getProductionLine = (
  orgId: string,
  factoryId: string,
  lineId: string
) =>
  https.get<GetProductionLineResponse>({
    url: `/v1/organizations/${orgId}/factories/${factoryId}/production-lines/${lineId}`,
  });

export const createProductionLine = (
  orgId: string,
  factoryId: string,
  payload: CreateProductionLinePayload
) =>
  https.post<GetProductionLineResponse>({
    url: `/v1/organizations/${orgId}/factories/${factoryId}/production-lines`,
    body: payload,
  });

export const updateProductionLine = (
  orgId: string,
  factoryId: string,
  lineId: string,
  payload: UpdateProductionLinePayload
) =>
  https.put<GetProductionLineResponse>({
    url: `/v1/organizations/${orgId}/factories/${factoryId}/production-lines/${lineId}`,
    body: payload,
  });

export const deleteProductionLine = (
  orgId: string,
  factoryId: string,
  lineId: string
) =>
  https.delete({
    url: `/v1/organizations/${orgId}/factories/${factoryId}/production-lines/${lineId}`,
  });

